import FileSaver from 'file-saver';
import _ from 'lodash';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { useRef, useState } from 'react';
import { Phase } from '../../../Helper/ApiHelper/KeyValueNetworkHelper';
import {
  CopyCatalogInfo,
  copyExternalLvFromPlatformToPhase,
  deleteLvCatalogForProperty,
  MinimizedLvCatalog,
  readLvCatalogAsExcelForProperty
} from '../../../Helper/ApiHelper/LvEpNetworkHelper';
import { DURATION_NOTIFICATION_ERROR_LONG } from '../../../Helper/Statics/Constants';
import { isBefore } from '../../../Helper/Util/TimelineStepHelper';
import BeeLinkButton from '../../Atoms/BeeLinkButton';
import BeeDeletionDialog from '../Dialogs/BeeDeletionDialog';
import AlphaLvCatalogSelectDialog from '../Dialogs/AlphaLvCatalogSelectDialog';
import './EpLvLiegenschaftLV.scss';

type EpLvLiegenschaftLVProps = {
  phase?: Phase;
  propertyId?: string;
  possibleLvCatalogs?: Record<string, MinimizedLvCatalog[]>;
  onAdaptData: (headline: string, message: string) => void;
};

export function EpLvLiegenschaftLV({
  phase,
  propertyId,
  possibleLvCatalogs,
  onAdaptData
}: EpLvLiegenschaftLVProps) {
  //states
  const [selectLvCatalogVisible, setSelectLvCatalogVisible] =
    useState<boolean>(false);
  const [deletionVisible, setDeletionVisible] = useState<boolean>(false);

  //refs
  const toast = useRef<Toast>(null);

  /////////////
  // DIALOGS //
  /////////////

  function injectSelectLvCatalogDialog() {
    if (!phase || !propertyId) return null;

    const catalogs = possibleLvCatalogs ?? {};

    return (
      <AlphaLvCatalogSelectDialog
        visible={selectLvCatalogVisible}
        header={'LV-Katalog auswählen'}
        catalogs={catalogs}
        epCatalogVersionId={
          (phase?.ep as unknown as CopyCatalogInfo)?.versionId ?? ''
        }
        readOnly={false}
        onHide={() => setSelectLvCatalogVisible(false)}
        onConfirm={(selection) => applySelectedLvCatalog(selection)}
      />
    );
  }

  function injectDeletionConfirmationDialog() {
    if (phase && propertyId) {
      const lv = _.find(phase.lvs, function (e: MinimizedLvCatalog) {
        return e.propertyId === propertyId;
      });
      if (lv) {
        return (
          <BeeDeletionDialog
            visible={deletionVisible}
            message={'Möchten Sie das LV dieser Liegenschaft wirklich löschen?'}
            messageIcon={'pi pi-exclamation-triangle'}
            acceptLabel={'Löschen'}
            rejectLabel={'Abbrechen'}
            header={'Leistungsverzeichnis löschen'}
            type={'primary'}
            onAccept={() => deleteLV(phase.id, propertyId, lv.id)}
            onReject={() => setDeletionVisible(false)}
            onHide={() => setDeletionVisible(false)}
          />
        );
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  ///////////////
  // UI HELPER //
  ///////////////

  function adaptData(headline: string, message: string) {
    if (onAdaptData) {
      onAdaptData(headline, message);
    }
  }

  function downloadLVCatalog(
    phaseId: String,
    propertyId: string,
    filename: string
  ) {
    readLvCatalogAsExcelForProperty(phaseId, propertyId)
      .then((result: any) => FileSaver.saveAs(result, filename))
      .catch((error) =>
        toast.current?.show({
          severity: 'error',
          summary: 'Download nicht möglich',
          detail:
            'Dieses LV kann leider nicht heruntergeladen werden. ' + error,
          sticky: false,
          closable: true,
          life: DURATION_NOTIFICATION_ERROR_LONG
        })
      );
  }

  function applySelectedLvCatalog(selection: MinimizedLvCatalog) {
    if (!phase || !propertyId) return;

    //selectLvCatalog for phase
    copyExternalLvFromPlatformToPhase(phase.id, propertyId, selection.id)
      .then((result) => {
        const message = result?.title
          ? 'Der LV-Katalog ' + result.title + ' wurde der Phase zugewiesen.'
          : 'Der LV-Katalog wurde der Phase zugewiesen.';
        adaptData('LV gespeichert', message);

        setSelectLvCatalogVisible(false);
      })
      .catch((error) => {
        toast.current?.show({
          severity: 'error',
          summary: 'LV auswählen fehlgeschlagen',
          detail: 'Das LV kann leider nicht ausgewählt werden. ' + error,
          sticky: false,
          closable: true,
          life: DURATION_NOTIFICATION_ERROR_LONG
        });

        setSelectLvCatalogVisible(false);
      });
  }

  function deleteLV(phaseId: String, propertyId: String, lvId: String) {
    if (!phase) return;
    deleteLvCatalogForProperty(phaseId, propertyId, lvId)
      .then(() => {
        //update stored phase
        let updatedPhase: Phase = _.cloneDeep(phase);
        if (updatedPhase && updatedPhase.lvs) {
          _.remove(updatedPhase.lvs, function (e: MinimizedLvCatalog) {
            return e.id === lvId;
          });
          const headline = 'LV gelöscht';
          const message =
            'Das Leistungsverzeichnis der Liegenschaft wurde erfolgreich gelöscht.';
          adaptData(headline, message);
        }
      })
      .catch((error) => {
        toast.current?.show({
          severity: 'error',
          summary: 'Löschen fehlgeschlagen',
          detail:
            'Das Leistungsverzeichnis kann leider nicht gelöscht werden. Bitte versuchen Sie es später erneut. ' +
            error,
          sticky: false,
          closable: true,
          life: DURATION_NOTIFICATION_ERROR_LONG
        });
      });
  }

  ////////////////
  // UI METHODS //
  ////////////////

  const injectUI = () => {
    if (phase && propertyId) {
      if (phase.end && isBefore(phase.end, new Date())) {
        if (phase.ep && phase.ep.id) {
          if (
            phase.lvs &&
            _.find(phase.lvs, function (e: MinimizedLvCatalog) {
              return e.propertyId === propertyId;
            }) !== undefined
          ) {
            //EP exists, LV exists
            return injectPhaseNotDoneWithEpAndLV(phase, propertyId);
          } else {
            //EP exists, LV not
            return injectPhaseNotDoneWithEpNoLV(phase);
          }
        } else {
          //No Ep no lv
          return injectPhaseNotDoneNoEpNoLV();
        }
      } else {
        if (
          phase.lvs &&
          _.find(phase.lvs, function (e: MinimizedLvCatalog) {
            return e.propertyId === propertyId;
          }) !== undefined
        ) {
          // there is an lv to download
          return injectPhaseDoneWithLV(phase, propertyId);
        }
        return injectPhaseDoneNoLV();
      }
    } // render empty otherwise!!
  };

  function injectPhaseDoneNoLV() {
    return <div>—</div>;
  }

  function injectPhaseDoneWithLV(phase: Phase, propertyId: String) {
    const lv: MinimizedLvCatalog | undefined = _.find(
      phase.lvs,
      function (e: MinimizedLvCatalog) {
        return e.propertyId === propertyId;
      }
    );
    const creation = lv && lv.createdAt ? new Date(lv.createdAt) : null;
    const filename: any = lv && lv.filename ? lv.filename : null;

    return (
      <div>
        {lv && lv.id ? (
          <>
            <BeeLinkButton
              label={filename}
              type={'secondary'}
              onClick={() =>
                downloadLVCatalog(phase.id, lv.propertyId, filename)
              }
            />
            <div>
              Stand: {creation ? creation.toLocaleDateString('de-DE') : null}
            </div>
          </>
        ) : null}
      </div>
    );
  }

  function injectPhaseNotDoneNoEpNoLV() {
    return (
      <div>
        <div className={'ep-lv-lvUpload-btn upload-lv-disabled'}>
          <BeeLinkButton
            label={
              <>
                <i className={'pi pi-upload mr-2'}></i>
                <span>LV auswählen</span>
              </>
            }
            type={'primary'}
            disabled={false /* true */}
            onClick={() => setSelectLvCatalogVisible(true)}
          />
        </div>
        <Tooltip target=".upload-lv-disabled" mouseTrack mouseTrackTop={10}>
          Bitte laden Sie einen EP-Katalog hoch.
        </Tooltip>
      </div>
    );
  }

  function injectPhaseNotDoneWithEpNoLV(phase: Phase) {
    return (
      <div className={'ep-lv-lvUpload-btn'}>
        {phase.ep && phase.ep.id ? (
          <BeeLinkButton
            label={
              <>
                {/*  <i className={'pi pi-upload mr-2'}></i> */}
                <span>LV auswählen</span>
              </>
            }
            type={'primary'}
            onClick={() => setSelectLvCatalogVisible(true)}
          />
        ) : null}
      </div>
    );
  }

  function injectPhaseNotDoneWithEpAndLV(phase: Phase, propertyId: String) {
    const lv: MinimizedLvCatalog | undefined = _.find(
      phase.lvs,
      function (e: MinimizedLvCatalog) {
        return e.propertyId === propertyId;
      }
    );
    const creation = lv && lv.createdAt ? new Date(lv.createdAt) : null;
    const filename: any = lv && lv.filename ? lv.filename : null;
    return (
      <div className={'ep-lv-lvDownload'}>
        {lv && lv.id ? (
          <div>
            <div className="grid">
              <div className={'col download-btn'}>
                <BeeLinkButton
                  label={filename}
                  type={'secondary'}
                  onClick={() =>
                    downloadLVCatalog(phase.id, lv.propertyId, filename)
                  }
                />
              </div>
              <div className={'col col-fixed delete-btn'}>
                <BeeLinkButton
                  label={<i className={'pi pi-trash'}></i>}
                  type={'danger'}
                  aria-label="Cancel"
                  onClick={() => setDeletionVisible(true)}
                />
              </div>
            </div>
            <div>
              Stand: {creation ? creation.toLocaleDateString('de-DE') : null}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <Toast ref={toast} position={'top-right'} />
      {injectUI()}
      {selectLvCatalogVisible ? injectSelectLvCatalogDialog() : null}
      {deletionVisible ? injectDeletionConfirmationDialog() : null}
    </div>
  );
}

export default EpLvLiegenschaftLV;
